import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  FileText,
  AlertTriangle,
  Pencil,
  Trash2,
  Check,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";

// ---------------------------------------------------------------------------
// Shared formatting helpers
// ---------------------------------------------------------------------------

function safeCurrencyCode(currency) {
  const normalized = String(currency || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : "USD";
}

function formatCurrency(amount, currency = "USD") {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: safeCurrencyCode(currency),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return dateStr.slice(0, 10);
}

function truncate(str, maxLen = 40) {
  if (!str) return "—";
  return str.length > maxLen ? str.slice(0, maxLen - 1) + "…" : str;
}

// ---------------------------------------------------------------------------
// Cashflow movement derivation — founder model: each document = ONE ledger line.
// Sign convention (investor cashflow): Distribution = cash IN (+), Call = cash OUT (−).
// Commitment + NAV-only statements are NOT cashflow movements (amount=null) but stay as
// viewable reference rows so the view-doc / edit shell is preserved unchanged.
// ponytail: distribution-first when a doc carries both a call and a distribution delta (rare);
// upgrade to net/split only if a real doc needs it. The rigorous per-position series + XIRR is
// computed server-side in pg_cashflow_service (the header KPIs), not here.
// ---------------------------------------------------------------------------

const MINUS = "−"; // U+2212 true minus sign

// Cashflow event types — the only contributions that are real standalone movements. Commitment / NAV /
// fees_cumulative / realized_pnl_cumulative / unrealized_pnl_cumulative are NOT movements and are excluded.
const CASHFLOW_EVENT_TYPES = new Set(["capital_call", "distribution", "recallable_distribution"]);

function deriveMovement(doc, isFirst = false) {
  // A ledger shows STANDALONE (period) events, not cumulative restatements. The per-doc deltas already are
  // period amounts. The position_after fallback is ONLY valid for the opening doc (prior = 0, so cumulative ==
  // period); on every LATER NAV-only month the delta is null because nothing flowed that period, and reading
  // position_after there renders the CARRIED cumulative as a phantom repeating "Distribution"/"Call" — the bug.
  const d = doc.deltas || {};
  const hasDelta = d.called != null || d.distributed != null;
  const base = hasDelta ? d : (isFirst ? (doc.position_after || {}) : {});
  const call = base.called, dist = base.distributed;
  const hasCall = call != null && call > 0;       // negative deltas (corrupt cumulative) are NOT calls
  const hasDist = dist != null && dist > 0;
  // When a doc carries both (e.g. the opening statement: a large call + a small distribution) show the
  // larger-magnitude flow — the material standalone movement for that line.
  if (hasCall && hasDist) {
    return Math.abs(call) >= Math.abs(dist)
      ? { type: "Call", amount: -call }
      : { type: "Distribution", amount: dist };
  }
  if (hasDist) return { type: "Distribution", amount: dist };
  if (hasCall) return { type: "Call", amount: -call };
  return { type: "NAV update", amount: null };     // statement / no cashflow change this period
}

function formatSigned(amount, currency) {
  if (amount == null) return null;
  return (amount < 0 ? MINUS : "+") + formatCurrency(Math.abs(amount), currency);
}

const TYPE_STYLES = {
  Distribution: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Call: "bg-rose-50 text-rose-700 border-rose-200",
  Commitment: "bg-indigo-50 text-indigo-700 border-indigo-200",
  "NAV update": "bg-gray-100 text-gray-500 border-gray-200",
};

// A row is a "no-change row" if all position deltas are null or 0
function isNoChange(deltas) {
  if (!deltas) return true; // first doc has null deltas — not truly no-change
  const vals = [
    deltas.commitment,
    deltas.called,
    deltas.distributed,
    deltas.nav,
    deltas.tvpi,
    deltas.dpi,
    deltas.rvpi,
  ];
  return vals.every((v) => v == null || v === 0);
}

// ---------------------------------------------------------------------------
// EDITABLE_EVENT_TYPES and EVENT_TYPE_META — duplicated here to keep the
// component self-contained (the canonical copy lives in DashboardPage.jsx
// but isn't exported).
// ---------------------------------------------------------------------------

const EDITABLE_EVENT_TYPES = [
  "capital_call",
  "distribution",
  "recallable_distribution",
  "commitment",
  "nav",
];

const CURRENCY_OPTIONS = ["USD", "EUR", "GBP", "ILS", "CHF", "JPY", "CAD", "AUD"];

const EVENT_TYPE_LABELS = {
  capital_call: "Capital Call",
  distribution: "Distribution",
  recallable_distribution: "Recallable Dist.",
  commitment: "Commitment",
  nav: "NAV Update",
  nav_snapshot: "NAV Snapshot",
};

// POC field label map — human-readable names for all stored field types.
const POC_FIELD_LABELS = {
  capital_called_cumulative: "Capital Called (ITD)",
  capital_contributed_cumulative: "Capital Contributed (ITD)",
  distributions_cumulative: "Distributions (ITD)",
  nav: "NAV",
  commitment_total: "Commitment",
  capital_call_event_amount: "Capital Call",
  distribution_event_amount: "Distribution",
  tax_withheld: "Tax Withheld",
};

function pocFieldLabel(type) {
  return POC_FIELD_LABELS[type] || EVENT_TYPE_LABELS[type] || type || "—";
}

// ---------------------------------------------------------------------------
// LedgerSubRow — inline edit row for a single ledger entry inside a document
// ---------------------------------------------------------------------------

// LedgerSubRowView renders one extracted-field or client-input sub-row.
// isExtracted=true → view-only; no edit/delete controls (guardrail: fix at source).
// isExtracted=false (client_input) → edit+delete controls shown.
function LedgerSubRowView({ entry, onEdit, onRemove, isExtracted = false }) {
  const isRejected = entry.status === "rejected";
  const label = pocFieldLabel(entry.event_type);

  return (
    <tr className={`border-t border-gray-100 bg-gray-50/50 text-[11px] ${isRejected ? "opacity-50" : ""}`}>
      <td className="px-3 py-1.5 pl-12 font-mono text-gray-500" colSpan={2}>
        <span className={isRejected ? "line-through" : ""}>{formatDate(entry.event_date)}</span>
        {isRejected && (
          <Badge variant="outline" className="ml-1.5 text-[9px] border-red-200 text-red-600">
            rejected
          </Badge>
        )}
      </td>
      <td className="px-3 py-1.5">
        <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px]">{label}</span>
        {isExtracted && (
          <span className="ml-1.5 px-1 py-0.5 rounded bg-gray-50 text-gray-400 text-[9px] border border-gray-200">
            extracted
          </span>
        )}
        {!isExtracted && (
          <span className="ml-1.5 px-1 py-0.5 rounded bg-blue-50 text-blue-600 text-[9px] border border-blue-200">
            you added
          </span>
        )}
      </td>
      <td className="px-3 py-1.5 font-mono text-right text-gray-700" colSpan={3}>
        {entry.amount != null ? (
          <>
            {formatCurrency(entry.amount, entry.currency || "USD")}
            <span className="ml-2 text-gray-400 font-normal">{entry.currency || "USD"}</span>
          </>
        ) : (
          <span className="text-gray-300">—</span>
        )}
        {entry.basis && (
          <div className="text-[9px] text-gray-400 font-normal mt-0.5 truncate max-w-[200px]" title={entry.basis}>
            {entry.basis}
          </div>
        )}
      </td>
      <td className="px-3 py-1.5 text-right">
        {isExtracted ? (
          /* Extracted rows are view-only — fix at the source document */
          <span
            className="text-[9px] text-gray-300 cursor-default"
            title="Extracted — correct at the source document"
          >
            view only
          </span>
        ) : (
          !isRejected && (
            <div className="inline-flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => onEdit(entry)}
                className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-700"
                title="Edit row"
                aria-label="Edit ledger row"
              >
                <Pencil className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={() => onRemove(entry)}
                className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                title="Remove row"
                aria-label="Remove ledger row"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          )
        )}
      </td>
    </tr>
  );
}

function LedgerSubRowEdit({ entry, busy, onSave, onCancel }) {
  const [date, setDate] = useState(entry.event_date || "");
  const [amount, setAmount] = useState(entry.amount != null ? String(entry.amount) : "");
  const [currency, setCurrency] = useState(entry.currency || "USD");
  const [eventType, setEventType] = useState(entry.event_type || "capital_call");

  const handleSave = () => {
    const patch = {};
    if (date && date !== entry.event_date) patch.event_date = date;
    if (amount !== "" && Number(amount) !== entry.amount) patch.amount = Number(amount);
    if (currency && currency !== entry.currency) patch.currency = currency;
    if (eventType && eventType !== entry.event_type) patch.event_type = eventType;
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    onSave(patch);
  };

  return (
    <tr className="border-t border-gray-100 bg-blue-50/40 text-[11px]">
      <td className="px-2 py-1.5 pl-12" colSpan={2}>
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-6 text-xs"
        />
      </td>
      <td className="px-2 py-1.5">
        <Select value={eventType} onValueChange={setEventType}>
          <SelectTrigger className="h-6 text-[11px] w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EDITABLE_EVENT_TYPES.map((t) => (
              <SelectItem key={t} value={t} className="text-xs">
                {EVENT_TYPE_LABELS[t] || t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-2 py-1.5" colSpan={2}>
        <Input
          type="number"
          value={amount}
          step="0.01"
          onChange={(e) => setAmount(e.target.value)}
          className="h-6 text-xs text-right"
        />
      </td>
      <td className="px-2 py-1.5">
        <Select value={currency} onValueChange={setCurrency}>
          <SelectTrigger className="h-6 text-[11px] w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CURRENCY_OPTIONS.map((c) => (
              <SelectItem key={c} value={c} className="text-xs">
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-2 py-1.5 text-right">
        <div className="inline-flex items-center gap-0.5">
          <button
            type="button"
            onClick={handleSave}
            disabled={busy}
            className="p-1 rounded hover:bg-emerald-50 text-emerald-600 disabled:opacity-50"
            title="Save"
            aria-label="Save edit"
          >
            <Check className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="p-1 rounded hover:bg-gray-200 text-gray-500 disabled:opacity-50"
            title="Cancel"
            aria-label="Cancel edit"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// DocumentRow — one table row per source document
// ---------------------------------------------------------------------------

function DocumentRow({
  doc,
  currency,
  isSelected,
  onSelectDoc,
  onMutationDone,
  buildCorrectPath,
  buildRejectPath,
  showRejected,
  onCorrect,
  onReject,
  isDuplicate,
  isFirst,
}) {
  const [expanded, setExpanded] = useState(false);
  const [editingLedgerId, setEditingLedgerId] = useState(null);
  const [removingEntry, setRemovingEntry] = useState(null);
  const [busy, setBusy] = useState(false);

  // Sub-rows: the doc carries `contributions` which are not full ledger rows.
  // For editing, we need the full row detail — but the document-timeline endpoint
  // returns contributions as:
  //   { event_type, amount_semantic, amount_cents, field_name, ledger_id }
  // We can reconstruct a minimal entry from contributions for inline editing.
  //
  // Position amounts are already in dollars (float) per the contract.
  const deltas = doc.deltas || null;
  const mv = deriveMovement(doc, isFirst);
  const signed = formatSigned(mv.amount, currency);

  // A monotonic cumulative (called / distributed) that DECREASED vs the prior document is impossible —
  // it flags a data issue (e.g. the Moonfare dual-source corruption: drawdown 'add' + statement 'set'
  // on the same field). Tint the line red so the anomaly is visible instead of hiding as a "NAV update".
  const anomaly = !!deltas && (
    (deltas.called != null && deltas.called < 0) ||
    (deltas.distributed != null && deltas.distributed < 0)
  );
  const anomalyTitle = "Cumulative value decreased vs the prior document — likely a data issue (mixed call sources).";
  const duplicateTitle = "Same movement amount appears on another date — flagged for review (possible duplicate).";

  const noChange = deltas !== null && isNoChange(deltas);
  const dimClass = noChange ? "opacity-60 text-muted-foreground" : "";

  const displayName =
    doc.original_filename ||
    doc.source_doc_type_raw ||
    doc.source_doc_id?.split("/").pop() ||
    "Unknown";

  const handleSaveEdit = async (contribution, patch) => {
    setBusy(true);
    try {
      // Demo/preview mode injects an in-memory handler; live mode POSTs to the ledger API.
      if (onCorrect) {
        await onCorrect(contribution, patch);
      } else {
        const path = buildCorrectPath(contribution.ledger_id);
        const payload = {
          amount: patch.amount,
          event_date: patch.event_date,
          currency: patch.currency,
          reason: "Dashboard edit",
        };
        await api.post(path, payload);
      }
      toast.success("Saved");
      setEditingLedgerId(null);
      if (onMutationDone) await onMutationDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmRemove = async (contribution) => {
    setBusy(true);
    try {
      if (onReject) {
        await onReject(contribution);
      } else {
        const path = buildRejectPath(contribution.ledger_id);
        await api.post(path, { reason: "Dashboard removal" });
      }
      toast.success("Removed");
      setRemovingEntry(null);
      if (onMutationDone) await onMutationDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  // Build a minimal entry shape for sub-row components from a contribution.
  // POC bridge contributions carry `amount` (float) directly; legacy format uses `amount_cents`.
  const toEntry = (c) => ({
    ledger_id: c.ledger_id,
    event_type: c.event_type,
    field_name: c.field_name,
    event_date: doc.event_date || "",
    amount: c.amount != null ? c.amount : (c.amount_cents != null ? c.amount_cents / 100 : null),
    currency: c.currency || currency,
    basis: c.basis || null,
    source: c.source || "extracted",
    status: "active",
  });

  return (
    <>
      {/* Main document row */}
      <tr
        className={`border-t cursor-pointer ${
          anomaly
            ? "border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-900"
            : isDuplicate
            ? "border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-900"
            : "border-gray-100 hover:bg-gray-50"
        } ${isSelected ? "border-l-2 border-l-blue-500" : ""} ${(anomaly || isDuplicate) ? "" : dimClass}`}
        onClick={() => setExpanded((v) => !v)}
        title={anomaly ? anomalyTitle : isDuplicate ? duplicateTitle : undefined}
        data-testid="doc-timeline-row"
        data-anomaly={anomaly ? "true" : undefined}
        data-dup-flag={isDuplicate ? "true" : undefined}
      >
        {/* Expand chevron */}
        <td className="px-2 py-2 w-6">
          {expanded ? (
            <ChevronDown className="w-3 h-3 text-gray-400" />
          ) : (
            <ChevronRight className="w-3 h-3 text-gray-400" />
          )}
        </td>

        {/* Date */}
        <td className="px-2 py-2 font-mono text-gray-600 whitespace-nowrap">
          {formatDate(doc.event_date)}
        </td>

        {/* Movement type */}
        <td className="px-2 py-2 whitespace-nowrap">
          <span className={`px-1.5 py-0.5 rounded border text-[10px] font-medium ${TYPE_STYLES[mv.type] || TYPE_STYLES["NAV update"]}`}>
            {mv.type}
          </span>
        </td>

        {/* Amount — the single signed cashflow column */}
        <td className="px-2 py-2 text-right font-mono whitespace-nowrap">
          {signed == null ? (
            <span className="text-gray-300">—</span>
          ) : (
            <span className={mv.amount < 0 ? "text-rose-600" : "text-emerald-600"}>{signed}</span>
          )}
        </td>

        {/* NAV (after) — cumulative NAV snapshot after this doc; reference context, not a movement */}
        <td className="px-2 py-2 text-right font-mono text-gray-500 whitespace-nowrap">
          {doc.position_after?.nav != null
            ? formatCurrency(doc.position_after.nav, currency)
            : <span className="text-gray-300">—</span>}
        </td>

        {/* Document name + discrepancy badge (de-emphasised; the view-doc affordance stays) */}
        <td className="px-2 py-2 max-w-[200px]">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="truncate text-gray-400" title={displayName}>
              {truncate(displayName, 40)}
            </span>
            {doc.discrepancies?.length > 0 && (
              <span
                title={`${doc.discrepancies.length} ${doc.discrepancies.length === 1 ? "discrepancy" : "discrepancies"}`}
                className="flex-shrink-0"
              >
                <AlertTriangle className="w-3 h-3 text-amber-500" />
              </span>
            )}
          </div>
        </td>

        {/* PDF icon — stops propagation to avoid row toggle */}
        <td
          className="px-2 py-2 text-right"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() =>
              onSelectDoc(isSelected ? null : doc.source_doc_id, doc)
            }
            className={`p-1 rounded ${
              isSelected
                ? "bg-blue-100 text-blue-600"
                : "hover:bg-gray-200 text-gray-400 hover:text-gray-700"
            }`}
            title="View source PDF"
            aria-label="View source PDF"
            data-testid="view-pdf-button"
          >
            <FileText className="w-3.5 h-3.5" />
          </button>
        </td>
      </tr>

      {/* Expanded sub-rows: ALL extracted fields per doc (P2 drilldown).
          Extracted rows are view-only (isExtracted=true). Client-input rows (source=client_input)
          show edit+delete controls. The guardrail: NEVER allow editing an extracted figure. */}
      {expanded &&
        (doc.contributions || []).map((c) => {
          const entry = toEntry(c);
          const isExtracted = !c.source || c.source === "extracted";
          const key = c.ledger_id || `${doc.source_doc_id}-${c.event_type}-${c.amount}`;

          if (!isExtracted && editingLedgerId === c.ledger_id) {
            return (
              <LedgerSubRowEdit
                key={key}
                entry={entry}
                busy={busy}
                onSave={(patch) => handleSaveEdit(c, patch)}
                onCancel={() => setEditingLedgerId(null)}
              />
            );
          }

          return (
            <LedgerSubRowView
              key={key}
              entry={entry}
              isExtracted={isExtracted}
              onEdit={isExtracted ? undefined : () => setEditingLedgerId(c.ledger_id)}
              onRemove={isExtracted ? undefined : () => setRemovingEntry(c)}
            />
          );
        })}

      {/* Remove confirm — inline (not a full dialog to keep this table-centric) */}
      {removingEntry && (
        <tr className="border-t border-gray-100 bg-red-50/40 text-[11px]">
          <td colSpan={7} className="px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-red-700 text-xs">
                Remove this {EVENT_TYPE_LABELS[removingEntry.event_type] || removingEntry.event_type} row?
                This will update the position. The row stays in the audit trail.
              </span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => handleConfirmRemove(removingEntry)}
                  disabled={busy}
                  className="px-2 py-0.5 rounded bg-red-600 text-white text-xs hover:bg-red-700 disabled:opacity-50"
                >
                  Remove
                </button>
                <button
                  type="button"
                  onClick={() => setRemovingEntry(null)}
                  disabled={busy}
                  className="px-2 py-0.5 rounded border border-gray-200 bg-white text-gray-700 text-xs hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// DocumentTimelineTable — the full table, one row per source document
// ---------------------------------------------------------------------------

/**
 * Props:
 *   documents          {array}    — from document-timeline endpoint
 *   currency           {string}   — fund currency code
 *   selectedDocId      {string|null}
 *   onSelectDoc        {fn(docId, doc)}
 *   onMutationDone     {fn}       — called after edit/reject to refresh
 *   buildCorrectPath   {fn(id)}   — returns POST path for corrections
 *   buildRejectPath    {fn(id)}   — returns POST path for rejections
 */
export default function DocumentTimelineTable({
  documents = [],
  currency = "USD",
  selectedDocId,
  onSelectDoc,
  onMutationDone,
  buildCorrectPath,
  buildRejectPath,
  onCorrect,
  onReject,
}) {
  if (documents.length === 0) {
    return (
      <div className="text-xs text-gray-400 py-3 px-3">
        No documents recorded for this investor.
      </div>
    );
  }

  // Duplicate flag (founder rule): a (movement-type, amount) appearing on MORE THAN ONE distinct date is
  // flagged on every occurrence for review (same amount + same date is one row, not a duplicate). Computed
  // client-side over the rendered movement rows — the /timeline payload doesn't carry the flag.
  const _mvKey = (doc, i) => {
    const mv = deriveMovement(doc, i === 0);
    return mv.amount == null ? null : `${mv.type}|${Math.round(mv.amount * 100)}`;
  };
  const _datesByKey = new Map();
  documents.forEach((doc, i) => {
    const k = _mvKey(doc, i);
    if (!k) return;
    if (!_datesByKey.has(k)) _datesByKey.set(k, new Set());
    _datesByKey.get(k).add(doc.event_date);
  });
  const _dupKeys = new Set([..._datesByKey.entries()].filter(([, ds]) => ds.size > 1).map(([k]) => k));
  const isDup = (doc, i) => { const k = _mvKey(doc, i); return !!k && _dupKeys.has(k); };

  return (
    <div className="overflow-x-auto overflow-y-auto">
      <table className="w-full text-xs" data-testid="document-timeline-table">
        <thead className="bg-gray-50 text-gray-500 uppercase sticky top-0 z-10">
          <tr>
            <th className="px-2 py-2 w-6" aria-label="Expand" />
            <th className="px-2 py-2 text-left font-medium w-24">Date</th>
            <th className="px-2 py-2 text-left font-medium w-28">Movement</th>
            <th className="px-2 py-2 text-right font-medium w-32">Amount</th>
            <th className="px-2 py-2 text-right font-medium w-28">NAV (after)</th>
            <th className="px-2 py-2 text-left font-medium">Document</th>
            <th className="px-2 py-2 text-right font-medium w-10" aria-label="PDF" />
          </tr>
        </thead>
        <tbody>
          {documents.map((doc, i) => (
            <DocumentRow
              key={doc.contributions?.[0]?.ledger_id || doc.source_doc_id}
              doc={doc}
              currency={currency}
              isSelected={selectedDocId === doc.source_doc_id}
              onSelectDoc={onSelectDoc}
              onMutationDone={onMutationDone}
              buildCorrectPath={buildCorrectPath}
              buildRejectPath={buildRejectPath}
              onCorrect={onCorrect}
              onReject={onReject}
              isDuplicate={isDup(doc, i)}
              isFirst={i === 0}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
